import React, { useEffect, useState } from "react";
import FiltersTrackCase, {
  FiltersSelectType,
  initialFilterSelect,
  SearchFormDataType,
} from "./FiltersTrackCase";
import { ColumnsType } from "antd/es/table";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import Datatable from "../../../core/common/dataTable";
import TimelineModal from "./TimelineModal";
import {
  parseNumber,
  safeLocaleCompare,
} from "../../../core/methods/sorterMethods";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import dayjs from "dayjs";
import { getSearchableHeader } from "../../../core/utils/getSearchableHeader";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";
import { exportAntdTableToExcel } from "../../../core/utils/exportExcel";
import { filterByColumnSearch } from "../../admin/utils/filterByColumnSearch";

type Props = {
  status_id?: string;
};

const StoppedCases: React.FC<Props> = ({ status_id }) => {
  const [itemList, setItemList] = useState([]);
  const [searchFilter, setSearchFilter] = useState<SearchFormDataType>({
    case_id: "",
    applicant_name: "",
    email: "",
    from_date: "",
    to_date: "",
  });

  const [filterData, setFilterData] =
    useState<FiltersSelectType>(initialFilterSelect);
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 1000,
    total: 0,
  });
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);
  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});

  type InviteItem = any;
  // const filteredData = (Array.isArray(itemList) ? itemList : []).filter(
  //   (item: InviteItem) => {
  //     return Object.entries(inventoryColumnSearch).every(([key, value]) => {
  //       if (!value) return true;

  //       return String(item[key as keyof InviteItem] ?? "")
  //         .toLowerCase()
  //         .includes(value.toLowerCase());
  //     });
  //   },
  // );
  const filteredData = filterByColumnSearch(
    Array.isArray(itemList) ? itemList : [],
    inventoryColumnSearch,
  );

  const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const {
    callApi: handlesSearch,
    data: dataList,
    loading: searchLoading,
  } = useApi(webservice_url.hr_reports, {
    method: "POST",
    autoCall: false,
  });

  const handleViewLog = async (caseId?: string) => {
    if (!caseId) return;
    setShowTimeline(true);
    setTimelineLoading(true);
    try {
      const res = await getCaseLog.callApi({ case_id: caseId });
      setTimelineData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const fetchData = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
  ) => {
    const payload = {
      status: status_id || null,
      org_ids: filterData?.organization || null,
      project_ids: filterData?.project || null,
      program_ids: filterData?.program || null,
      department_ids: filterData?.departments || null,
      page,
      limit: pageSize,
    };

    try {
      const response = await handlesSearch(payload);

      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: response?.data?.total || 0,
      }));
    } catch (error) {
      console.error("API Failed", error);
    }
  };

  const handleTableChange = async (pagination: any) => {
    setPagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const handleSearch = () => {
    if (pagination.current !== 1) {
      // pagination badalne par useEffect khud fetch karega
      setPagination((prev) => ({ ...prev, current: 1 }));
    } else {
      fetchData(1, pagination.pageSize);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      onCell: () => cellWidth(100),
      align: "center",
    },
    {
      title: getSearchableHeader(
        "Applicant Name",
        "applicant_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "applicant_name",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "applicant_name"),
    },
    {
      title: getSearchableHeader(
        "Case Id",
        "case_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_id",
      onCell: () => cellWidth(190),
      sorter: (a, b) => parseNumber(a.caseId) - parseNumber(b.case_id),
    },
    {
      title: getSearchableHeader(
        "Email Id",
        "email_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "email_id",
      onCell: () => cellWidth(220),
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "email_id"),
    },
    {
      title: getSearchableHeader(
        "Check Name",
        "check_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "check_name",
      onCell: () => cellWidth(220),
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "check_name"),
    },
    {
      title: getSearchableHeader(
        "Project",
        "project_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Program",
        "program_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Spoc Name",
        "case_spoc",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_spoc",
      onCell: () => cellWidth(220),
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "email_id"),
    },
   {
      title: getSearchableHeader(
        "Case Status",
        "overall_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "overall_status",
      key: "overall_status",
      onCell: () => cellWidth(140),
      sorter: (a: any, b: any) =>
        (a.overall_status ?? "").localeCompare(b.overall_status ?? ""),
      render: (text: any, record: any) => {
   
    return (
      <span
        style={{
          backgroundColor: record?.overall_color_value || "#6c757d",
          color: "#fff",
          padding: "5px 10px",
          borderRadius: "4px",
          display: "inline-block",
          fontSize: "12px",
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        {text ?? "-"}
      </span>
    );
  },
    },
    {
      title: getSearchableHeader(
        "Stopped At",
        "stopped_at",
        setInventoryColumnSearch,
      ),
      dataIndex: "stopped_at",
      onCell: () => cellWidth(220),
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "stopped_at"),
      render: (e: any) => dayjs().format("DD-MM-YYYY"),
    },
    // {
    //   title: "Total checks",
    //   dataIndex: "totalChecks",
    //   width: 130,
    //   align: "center",
    //   sorter: (a: any, b: any) =>
    //     parseNumber(a.totalChecks) - parseNumber(b.totalChecks),
    // },
    // {
    //   title: "Stopped checks",
    //   dataIndex: "stoppedChecks",
    //   width: 150,
    //   align: "center",
    //   sorter: (a: any, b: any) =>
    //     parseNumber(a.stoppedChecks) - parseNumber(b.stoppedChecks),
    // },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   width: 120,
    //   align: "center",
    //   render: (status: string) => (
    //     <span
    //       className={`badge ${
    //         status === "Stopped"
    //           ? "bg-dark"
    //           : status === "Completed"
    //             ? "bg-success"
    //             : "bg-warning"
    //       }`}
    //     >
    //       {status}
    //     </span>
    //   ),
    //   sorter: (a: any, b: any) => safeLocaleCompare(a, b, "status"),
    // },
    // {
    //   title: "Processing Time",
    //   dataIndex: "processingTime",
    //   width: 150,
    //   align: "center",
    //   sorter: (a, b) =>
    //     parseTiming(a.processingTime) - parseTiming(b.processingTime),
    // },
    // {
    //   title: "Action",
    //   dataIndex: "action",
    //   width: 140,
    //   render: (_: any, record: any) => (
    //     <button
    //       type="button"
    //       className="btn btn-success btn-sm"
    //     // onClick={() => handleResume(record)}
    //     >
    //       Resume
    //     </button>
    //   ),
    // },
    // {
    //   title: "View Log",
    //   dataIndex: "viewLog",
    //   width: 100,
    //   render: () => (
    //     <div
    //       className="text-center text-dark"
    //       style={{ cursor: "pointer" }}
    //       onClick={() => setShowTimeline(true)}
    //     >
    //       <i className="ti ti-eye "></i>
    //     </div>
    //   ),
    // },
    {
      title: "View Log",
      key: "viewLog",
      onCell: () => cellWidth(110),
      render: (_: any, record: any) => (
        <div
          className="text-center text-dark"
          style={{ cursor: "pointer" }}
          onClick={() => handleViewLog(record.case_id)}
        >
          <i className="ti ti-eye"></i>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (dataList?.data?.rows) {
      setItemList(dataList.data.rows);
    }
  }, [dataList]);

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize);
  }, [status_id, pagination.current, pagination.pageSize]);

  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "Stopped_Cases_Report",
      sheetName: "Stoppe Cases",
    });
  };

  const loading = searchLoading;
  return (
    // <div className="page-wrapper">
    //   <div className="content">
    <div>
      {/* filters */}
      <FiltersTrackCase
        filterData={filterData}
        searchFilter={searchFilter}
        setFilterData={setFilterData}
        setSearchFilter={setSearchFilter}
        onSearch={handleSearch}
      />

      {/* Table  */}
      <div className="card mt-3">
        {searchLoading ? (
          <SnipperLoader />
        ) : (
          <>
            <div className="d-flex justify-content-end pe-2 py-2 pb-0">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleDownloadExcel}
                disabled={loading}
              >
                <i className="ti ti-download" /> Export to Excel
              </button>
            </div>
            <Datatable
              dataSource={filteredData}
              columns={columns}
              Selection={false}
              fixedHeader
              tableHeight={650}
              pagination={pagination}
              handleTableChange={handleTableChange}
            />
          </>
        )}
      </div>

      {/* Timeline modal */}
      {showTimeline && (
        <TimelineModal
          timelineData={timelineData}
          loading={timelineLoading}
          onClose={() => {
            setShowTimeline(false);
            setTimelineData([]);
          }}
        />
      )}
    </div>
    //   </div>
    //   {/* ---- Footer ---- */}
    //   <Footer />
    // </div>
  );
};

export default StoppedCases;
